import { Badge, Button } from "@roberttamas/ui";
import { sendEmailVerification } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { toAppError } from "@/lib/errors";

/**
 * Inline "email not verified" indicator with a resend affordance.
 * Renders nothing for verified users (and Google accounts, which are
 * always verified). Verification is not a wall — this is the only
 * place the pending state surfaces. It clears automatically when the
 * user verifies in another tab and refocuses this one (the auth
 * provider reloads the profile on focus).
 */
export function EmailVerificationBadge() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await sendEmailVerification(user);
      setSent(true);
      toast.success(t("emailVerification.toastSent"));
    } catch (err) {
      toast.error(toAppError(err).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <span className="flex items-center gap-1.5">
      <Badge variant="outline" className="text-muted-foreground">
        {t("emailVerification.badge")}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={sending}
        onClick={() => void resend()}
      >
        {sending
          ? t("emailVerification.sending")
          : sent
            ? t("emailVerification.sent")
            : t("emailVerification.resend")}
      </Button>
    </span>
  );
}
